import type { ExistingOrder, ValuationStatus } from '@einwert/valuation-api-client'
import { useAllReportsZipUrl } from 'ClientPortal/src/api/Queries/files'
import { useGetOrders } from 'ClientPortal/src/api/Queries/orders'
import { useGetValuations } from 'ClientPortal/src/api/Queries/valuations'
import { useGetValueChecks } from 'ClientPortal/src/api/Queries/value-checks'
import { useGetAppraiserUsers } from 'ClientPortal/src/api/Queries/users'
import { downloadFileFromLink } from 'ClientPortal/src/common/utils/files'
import { useTypedParams } from 'ClientPortal/src/router/useTypedParams'
import type { CustomerExistingValuation as ExistingValuation, RootUser as User } from '@einwert/valuation-api-client'
import { ReadValueCheck } from '@einwert/valuation-api-client'
import { groupBy, map, maxBy, some, sortBy } from 'lodash'

export type OrderWithValuation = {
    order?: ExistingOrder
    valuation?: ExistingValuation
    valueCheck?: ReadValueCheck
    appraiserUsers?: User[]
    parentValuationStatus?: ValuationStatus
}

export const getFinalisedValuationIds = (ordersWithValuations: OrderWithValuation[]): string[] =>
    Array.from(
        new Set(
            ordersWithValuations
                .map(({ valuation }) => (valuation?.status === 'finalised' ? valuation.uuid : undefined))
                .filter(Boolean) as string[]
        )
    )

export const getFinalisedValuations = (ordersWithValuations: OrderWithValuation[]): ExistingValuation[] =>
    ordersWithValuations
        .map(({ valuation }) => valuation)
        .filter((valuation): valuation is ExistingValuation => valuation?.status === 'finalised')

export function useAssetValuationList() {
    const { assetId } = useTypedParams(['assetId'])
    const { valuations: allValuations, isValuationsPending } = useGetValuations({
        assetId,
        includeDrafts: true,
        filterFinalized: false,
    })
    const valuations = allValuations.filter((valuation) => valuation.status === 'finalised' || valuation.is_draft)
    const { valueChecks, isValueChecksPending } = useGetValueChecks({ assetId })
    const { orders, isOrdersPending } = useGetOrders({ assetId })
    const { appraiserUsers, isAppraiserUsersPending } = useGetAppraiserUsers()
    const { generateAllReportsZipUrl, isGeneratingAllReportsZipUrl, isErrorGeneratingAllReportsZipUrl } = useAllReportsZipUrl({
        assetId,
        onSuccess: (data) => downloadFileFromLink(data),
    })

    const valuationsByOrder = groupBy(valuations, 'order_uuid')

    const unsortedOrdersWithValuations: OrderWithValuation[] = map(valuationsByOrder, (matchingValuations, orderId) => {
        const finalizedValuation = matchingValuations.find((matchingValuation) => matchingValuation.status === 'finalised')
        const valuation = finalizedValuation ?? maxBy(matchingValuations, 'created_on')

        const parentValuationForDraft = valuation?.is_draft
            ? allValuations.find((matchingValuation) => !matchingValuation.is_draft && matchingValuation.drafts.includes(valuation.uuid))
            : valuation

        return {
            order: orders.find((order) => order.uuid === orderId),
            valuation,
            parentValuationStatus: parentValuationForDraft?.status,
        }
    })

    valueChecks.forEach((valueCheck) => {
        unsortedOrdersWithValuations.push({
            order: orders.find((order) => order.uuid === valueCheck.order_uuid),
            valueCheck,
        })
    })

    orders.forEach((order) => {
        if (unsortedOrdersWithValuations.findIndex((orderWithValuation) => orderWithValuation.order?.uuid === order.uuid) === -1) {
            unsortedOrdersWithValuations.push({ order })
        }
    })

    const ordersWithValuations = sortBy(
        unsortedOrdersWithValuations,
        ({ order, valuation, valueCheck }) => order?.valuation_date ?? valuation?.valuation_date ?? valueCheck?.valuation_date
    ).toReversed()

    const finalisedValuations = getFinalisedValuations(ordersWithValuations)
    const hasAtLeastOneExportableValuation = some(
        finalisedValuations.map((valuation) => valuation.capabilities?.valxml_exportable.valxml_v1_1)
    )

    return {
        ordersWithValuations,
        isValuationsListPending: isValuationsPending || isValueChecksPending || isOrdersPending || isAppraiserUsersPending,
        assetId,
        downloadAllReports: generateAllReportsZipUrl,
        isGeneratingAllReportsZipUrl,
        isErrorGeneratingAllReportsZipUrl,
        appraiserUsers,
        hasAtLeastOneExportableValuation,
    }
}
